import React, { useEffect, useMemo, useState } from 'react';
import { createTrackedLink, shortLinkUrl } from '../lib/api';
import { toast } from 'sonner';
import { X, Link2, Copy, Twitter, Facebook, Linkedin, MessageCircle, Send, Code2, CheckCheck } from 'lucide-react';

const SOCIALS = [
  { id: 'twitter',  label: 'X / Twitter', icon: Twitter,       color: '#1da1f2',
    url: (u, t) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
  { id: 'facebook', label: 'Facebook',    icon: Facebook,      color: '#1877f2',
    url: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { id: 'linkedin', label: 'LinkedIn',    icon: Linkedin,      color: '#0a66c2',
    url: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
  { id: 'whatsapp', label: 'WhatsApp',    icon: MessageCircle, color: '#25d366',
    url: (u, t) => `https://wa.me/?text=${encodeURIComponent(t + ' ' + u)}` },
  { id: 'telegram', label: 'Telegram',    icon: Send,          color: '#26a5e4',
    url: (u, t) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
  { id: 'reddit',   label: 'Reddit',      icon: MessageCircle, color: '#ff4500',
    url: (u, t) => `https://reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}` },
];

export default function ShareDialog({ open, onClose, video }) {
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const [trackedUrl, setTrackedUrl] = useState('');
  const [source, setSource] = useState('direct');
  const [campaign, setCampaign] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState('');

  const title = video?.title || 'Check this out on VidCreForge';

  const embed = useMemo(() => {
    const embedUrl = `${window.location.origin}/watch/${video?.id || ''}?embed=1`;
    return `<iframe width="560" height="315" src="${embedUrl}" title="${title.replace(/"/g, '&quot;')}" frameborder="0" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>`;
  }, [video, title]);

  useEffect(() => { if (!open) { setTrackedUrl(''); setCopied(''); } }, [open]);

  const doCopy = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag); toast.success('Copied to clipboard');
      setTimeout(() => setCopied(''), 1500);
    } catch { toast.error('Copy failed'); }
  };

  const makeTracked = async (platformId) => {
    if (!video?.id) return toast.error('No video to link');
    try {
      setBusy(true);
      const link = await createTrackedLink({
        destination_url: pageUrl,
        source: platformId || source,
        medium: 'share',
        campaign: campaign || `share-${new Date().toISOString().slice(0, 10)}`,
        video_id: video.id,
      });
      const shortUrl = shortLinkUrl(link.slug);
      setTrackedUrl(shortUrl);
      if (platformId) {
        const soc = SOCIALS.find(s => s.id === platformId);
        if (soc) window.open(soc.url(shortUrl, title), '_blank', 'noopener');
      }
      toast.success('Tracked link created');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create link');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="share-dialog-overlay">
      <div className="w-full max-w-[620px] max-h-[92vh] overflow-y-auto bg-[#121217] border border-white/10 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-testid="share-dialog">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="font-display text-[22px]">Share</div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="share-dialog-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          <div>
            <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2">Quick share</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {SOCIALS.map(s => (
                <button key={s.id} onClick={() => makeTracked(s.id)} disabled={busy}
                  className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 transition disabled:opacity-50"
                  data-testid={`share-social-${s.id}`}>
                  <s.icon className="w-5 h-5" style={{ color: s.color }} />
                  <span className="text-[11.5px] text-white/75">{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Tracked link (with analytics)</div>
            <div className="grid sm:grid-cols-2 gap-2 mb-2">
              <input value={source} onChange={e => setSource(e.target.value)} placeholder="source (e.g. bio)"
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-[13.5px] focus:outline-none focus:border-[#ff2e63]/50" data-testid="share-source-input" />
              <input value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="campaign (optional)"
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-[13.5px] focus:outline-none focus:border-[#ff2e63]/50" data-testid="share-campaign-input" />
            </div>
            <div className="flex items-stretch gap-2">
              <input readOnly value={trackedUrl || pageUrl}
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white/85 font-mono" data-testid="share-url-input" />
              {!trackedUrl ? (
                <button onClick={() => makeTracked(null)} disabled={busy} className="btn-primary" data-testid="share-generate-link-btn">
                  <Link2 className="w-4 h-4" /> Generate
                </button>
              ) : (
                <button onClick={() => doCopy(trackedUrl, 'url')} className="btn-ghost" data-testid="share-copy-url-btn">
                  {copied === 'url' ? <CheckCheck className="w-4 h-4 text-[#22d3ee]" /> : <Copy className="w-4 h-4" />} Copy
                </button>
              )}
            </div>
            <div className="text-[12px] text-white/50 mt-2">Track every click in <span className="text-[#ff6b8a]">Marketing Suite</span>.</div>
          </div>

          <div>
            <div className="text-[12px] uppercase tracking-wider text-white/45 mb-2 flex items-center gap-1.5"><Code2 className="w-3.5 h-3.5" /> Embed on your site</div>
            <textarea readOnly value={embed} rows={3}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white/80 font-mono" data-testid="share-embed-code" />
            <button onClick={() => doCopy(embed, 'embed')} className="btn-ghost mt-2" data-testid="share-copy-embed-btn">
              {copied === 'embed' ? <CheckCheck className="w-4 h-4 text-[#22d3ee]" /> : <Copy className="w-4 h-4" />} Copy embed code
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
